import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader, Card, Badge, EmptyState } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { FileText, Plus, Download, Pencil, Printer, ChevronRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { fmtINR, openPdf } from "@/lib/documents";
import { DownloadPdfButton } from "@/components/documents/DownloadPdfButton";
import { useAuth } from "@/contexts/AuthContext";
import { useHotelContext } from "@/contexts/HotelContext";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const quotationPdfUrl = (id) => `${API_BASE}/quotations/${id}/pdf`;
export const STATUS_TONE = { draft: "neutral", sent: "info", accepted: "success", rejected: "error", expired: "warning" };
export const fmtD = (v) => (v ? new Date(v.slice(0, 10) + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function Quotations() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { ctx } = useHotelContext();
  const activeHotelId = ctx?.hotel_id || "";
  const [data, setData] = useState({ items: [], total: 0, status_counts: {} });
  const [meta, setMeta] = useState({ types: [] });
  const [filters, setFilters] = useState({ status: "", quotation_type: "", from_date: "", to_date: "", q: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/quotations/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      if (activeHotelId) params.hotel_id = activeHotelId;
      const { data: d } = await api.get("/quotations/", { params: { ...params, page_size: 100 } });
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [filters, activeHotelId]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v === "all" ? "" : v }));
  const counts = data.status_counts || {};

  return (
    <div className="space-y-6" data-testid="quotations-page">
      <PageHeader title="Quotations" subtitle={`${data.total} quotation${data.total === 1 ? "" : "s"} · Proposals for prospective customers`}
        action={hasPermission("quotations.create") && (
          <Button onClick={() => navigate("/quotations/new")} data-testid="new-quotation-button"><Plus className="w-4 h-4 mr-2" />New Quotation</Button>
        )} />

      <div className="flex flex-wrap gap-2" data-testid="quotation-status-chips">
        {["", "draft", "sent", "accepted", "rejected", "expired"].map((s) => (
          <button key={s || "all"} type="button" onClick={() => setFilters((f) => ({ ...f, status: s }))} data-testid={`status-chip-${s || "all"}`}
            aria-pressed={filters.status === s}
            className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${filters.status === s ? "bg-[#DBFCEF] text-[#014751] border-[#014751] ring-1 ring-[#014751] shadow-sm" : "bg-white text-hocp-textSecondary border-hocp-border hover:border-hocp-deepTeal hover:text-hocp-deepTeal"}`}>
            {s ? s[0].toUpperCase() + s.slice(1) : "All"} <span className={`mono ml-1 px-1.5 rounded-full text-[11px] ${filters.status === s ? "bg-[#014751] text-[#DBFCEF]" : "bg-hocp-softIce text-hocp-deepTeal"}`}>{s ? counts[s] ?? 0 : Object.values(counts).reduce((a, b) => a + b, 0)}</span>
          </button>
        ))}
      </div>

      <Card className="p-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
          <Input placeholder="Search number / customer" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} data-testid="quotation-search" />
          <Select value={filters.quotation_type || "all"} onValueChange={set("quotation_type")}>
            <SelectTrigger data-testid="filter-quotation-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All types</SelectItem>{meta.types.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.status || "all"} onValueChange={set("status")}>
            <SelectTrigger data-testid="filter-quotation-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{["draft", "sent", "accepted", "rejected", "expired"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <DatePicker value={filters.from_date} onChange={set("from_date")} placeholder="From date" data-testid="filter-from-date" />
          <DatePicker value={filters.to_date} onChange={set("to_date")} placeholder="To date" minDate={filters.from_date || undefined} data-testid="filter-to-date" />
        </div>
      </Card>

      {!loading && !data.items.length && (
        <EmptyState icon={FileText} title="No quotations yet" subtitle="Create a quotation to send a customised proposal to a prospective customer."
          action={hasPermission("quotations.create") && <Button onClick={() => navigate("/quotations/new")} data-testid="empty-new-quotation">New Quotation</Button>} />
      )}

      {data.items.length > 0 && (
        <Card className="overflow-hidden">
          <table className="w-full" data-testid="quotations-table">
            <thead className="bg-hocp-softIce border-b border-hocp-border">
              <tr>{["Number", "Customer", "Type", "Date", "Valid until", "Total", "Status", "Actions"].map((h) => (
                <th key={h} className={`px-4 py-3 text-[12px] uppercase tracking-[0.08em] font-semibold text-hocp-deepTeal text-left ${["Type", "Valid until"].includes(h) ? "hidden lg:table-cell" : ""}`}>{h}</th>))}</tr>
            </thead>
            <tbody>
              {data.items.map((q) => (
                <tr key={q.id} className="border-b border-hocp-borderSubtle last:border-0 hover:bg-hocp-tableHover" data-testid={`quotation-row-${q.number}`}>
                  <td className="px-4 py-3 mono"><button type="button" onClick={() => navigate(`/quotations/${q.id}`)} className="font-semibold text-hocp-orangeStrong hover:underline" data-testid={`open-${q.number}`}>{q.number}</button></td>
                  <td className="px-4 py-3"><div className="font-medium text-hocp-textPrimary">{q.customer?.name}</div><div className="text-[11.5px] text-hocp-textMuted">{q.customer?.phone || q.customer?.email || ""}</div></td>
                  <td className="px-4 py-3 text-[12.5px] hidden lg:table-cell">{q.quotation_type_label}</td>
                  <td className="px-4 py-3 mono text-[12.5px] text-hocp-textSecondary">{fmtD(q.quotation_date)}</td>
                  <td className="px-4 py-3 mono text-[12.5px] text-hocp-textSecondary hidden lg:table-cell">{fmtD(q.valid_until)}</td>
                  <td className="px-4 py-3 mono font-semibold" data-testid={`total-${q.number}`}>{fmtINR(q.grand_total)}</td>
                  <td className="px-4 py-3"><Badge tone={STATUS_TONE[q.status] || "neutral"} data-testid={`status-${q.number}`}>{q.status}</Badge></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/quotations/${q.id}`)} data-testid={`edit-${q.number}`}><Pencil className="w-3 h-3 mr-1" />{hasPermission("quotations.edit") && !["accepted", "rejected"].includes(q.status) ? "Edit" : "Open"}</Button>
                      <Button size="sm" variant="outline" title="Print" onClick={() => openPdf(quotationPdfUrl(q.id), `Quotation-${q.number}.pdf`)} data-testid={`print-${q.number}`}><Printer className="w-3 h-3" /></Button>
                      <DownloadPdfButton url={quotationPdfUrl(q.id)} filename={`Quotation-${q.number}.pdf`} label="Download PDF" iconOnly className="!h-8 md:!h-8 !min-h-0 w-8 justify-center px-0" testId={`download-${q.number}`} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
